import { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DROPDOWNS = {
  home: { title: "Home", items: [] },
  about: { title: "About Us", items: [] },
  services: {
    title: "Our Services",
    items: [
      {
        heading: "Comprehensive Solutions",
        links: [
          "Deployment & Configuration",
          "Data Cleaning & Migration",
          "Staff Training & Onboarding",
          "API Integration & Automation",
          "Maintenance & Technical Support",
          "Data Management & Optimization",
          "Hiring, Payroll & HR Operations",
        ],
      },
    ],
  },
  products: {
    title: "Our Products",
    items: [
      {
        heading: "Our Products",
        links: ["Vetri Dashboard"],
      },
    ],
  },
};

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState({});
  const [mobileSubDropdown, setMobileSubDropdown] = useState({});
  const navigate = useNavigate();
  const hideTimeoutRef = useRef(null);

  const handleMouseEnter = (key) => {
    clearTimeout(hideTimeoutRef.current);
    setDropdownOpen(key);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 150);
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdown((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleMobileSubDropdown = (section) => {
    setMobileSubDropdown((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const servicesRef = useRef(null);
  const hireWriterRef = useRef(null);
  const planeRef = useRef(null);
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    if (
      startAnimation &&
      servicesRef.current &&
      hireWriterRef.current &&
      planeRef.current
    ) {
      const startRect = servicesRef.current.getBoundingClientRect();
      const endRect = hireWriterRef.current.getBoundingClientRect();
      const plane = planeRef.current;

      const deltaX = endRect.left - startRect.left;
      const deltaY = endRect.top - startRect.top;

      plane.style.left = `${startRect.left}px`;
      plane.style.top = `${startRect.top}px`;
      plane.style.opacity = 1;

      plane.animate(
        [
          {
            offset: 0,
            transform: `translate(0px, 190px) scale(1) rotate(-80deg)`,
            opacity: 1,
          },
          {
            offset: 0.5,
            transform: `translate(${deltaX / 2}px, 130px) scale(1.05) rotate(-50deg)`,
            opacity: 1,
          },
          {
            offset: 1,
            transform: `translate(${deltaX}px, ${deltaY}px) scale(1.1) rotate(-65deg)`,
            opacity: 0,
          },
        ],
        {
          duration: 3000,
          easing: "ease-in-out",
          fill: "forwards",
        },
      );

      setTimeout(() => setStartAnimation(false), 2100);
    }
  }, [startAnimation]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
  }, [isOpen]);

  return (
    <>
      <nav
        className="bg-white sticky top-0 px-[5%] z-50 transition-colors duration-500"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-16 flex-wrap gap-y-4 md:gap-y-0">
            {/* Logo */}
            <div
              onClick={() => navigate("/")}
              className="cursor-pointer flex items-center gap-2"
            >
              <img
                src="./images/EduLynks-logo.png"
                alt="Logo"
                className="h-10 sm:h-12 rotate"
              />
            </div>

            {/* Desktop Navigation */}
            <div
              className="hidden lg:flex items-center space-x-8 text-sm xl:text-base"
              ref={servicesRef}
            >
              {["home", "about", "services", "products"].map((key) => {
                const data = DROPDOWNS[key];
                const hasDropdown = data.items.length > 0;

                // ✅ Route map for all dropdown items
                const routeMap = {
                  "Deployment & Configuration": "/deployment-configuration",
                  "Data Cleaning & Migration": "/data-cleaning-migration",
                  "Staff Training & Onboarding": "/staff-training-onboarding",
                  "API Integration & Automation": "/api-integration-automation",
                  "Maintenance & Technical Support": "/maintenance-support",
                  "Data Management & Optimization": "/data-management",
                  "Hiring, Payroll & HR Operations": "/hiring-payrolls",
                  "Vetri Dashboard": "/our-product-vetri",
                };

                return (
                  <div
                    key={key}
                    className="relative"
                    onMouseEnter={() => {
                      if (hasDropdown) {
                        handleMouseEnter(key);
                        setStartAnimation(true);
                      }
                    }}
                    onMouseLeave={hasDropdown ? handleMouseLeave : null}
                  >
                    {/* Main Button */}
                    <button
                      onClick={() => {
                        if (!hasDropdown) {
                          if (key === "home") {
                            navigate("/");
                          } else if (key === "about") {
                            navigate("/about-us");
                          }
                        }
                      }}
                      className="flex items-center gap-1 text-black hover:scale-105 transition"
                    >
                      {data.title}
                      {hasDropdown &&
                        (dropdownOpen === key ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        ))}
                    </button>

                    {/* Dropdown Menu */}
                    {hasDropdown && dropdownOpen === key && (
                      <div className="absolute left-0 top-full bg-white shadow-lg rounded-xl mt-2 p-4 z-20 min-w-[280px] border-2 border-[#0d6b68]">
                        {data.items.map((section, index) => (
                          <div key={index} className="mb-3 last:mb-0">
                            {section.heading && (
                              <h4 className="font-semibold text-gray-700 mb-2">
                                {section.heading}
                              </h4>
                            )}
                            <ul className="space-y-1">
                              {section.links.map((link, idx) => (
                                <li
                                  key={idx}
                                  className="text-gray-700 py-1 text-sm hover:text-[#0d6b68] hover:translate-x-1 cursor-pointer transition"
                                  onClick={() => {
                                    if (routeMap[link]) {
                                      navigate(routeMap[link]);
                                      handleMouseLeave();
                                    }
                                  }}
                                >
                                  {link}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <button
                onClick={() => navigate("/contact-us")}
                className="btn2"
                ref={hireWriterRef}
              >
                Contact Us
              </button>
            </div>

            {/* Mobile Hamburger */}
            <div className="lg:hidden ml-auto">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-black hover:scale-105"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
      >
        <div
          className={`absolute inset-0 bg-black ${
            isOpen ? "opacity-50" : "opacity-0"
          } transition-opacity`}
        />
        <div
          className={`absolute top-1/2 left-1/2 w-80 max-w-[85vw] bg-[rgb(240_237_237)] shadow-3xl rounded-2xl z-50 transform transition-all duration-500 ease-in-out ${
            isOpen
              ? "translate-x-[-50%] translate-y-[-50%] opacity-100 scale-100"
              : "translate-x-[-50%] translate-y-[-50%] opacity-0 scale-90"
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold">Menu</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-6 space-y-2">
            {Object.entries(DROPDOWNS).map(([key, data]) => (
              <div key={key}>
                <button
                  onClick={() => {
                    if (data.items.length > 0) {
                      toggleMobileDropdown(key);
                    } else {
                      if (key === "home") {
                        navigate("/");
                      } else if (key === "about") {
                        navigate("/about-us");
                      }
                    }
                  }}
                  className="flex justify-between items-center w-full text-left px-4 py-2 text-gray-900 hover:scale-105"
                >
                  {data.title}
                  {data.items.length > 0 &&
                    (mobileDropdown[key] ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </button>

                {mobileDropdown[key] && data.items.length > 0 && (
                  <ul className="ml-4 mt-1 space-y-1 text-sm text-gray-600">
                    {data.items
                      .flatMap((section) => section.links)
                      .map((link, idx) => {
                        // ✅ Map link text to route path
                        const routeMap = {
                          "Deployment & Configuration":
                            "/deployment-configuration",
                          "Data Cleaning & Migration":
                            "/data-cleaning-migration",
                          "Staff Training & Onboarding":
                            "/staff-training-onboarding",
                          "API Integration & Automation":
                            "/api-integration-automation",
                          "Maintenance & Technical Support":
                            "/maintenance-support",
                          "Data Management & Optimization": "/data-management",
                          "Hiring, Payroll & HR Operations": "/hiring-payrolls",
                           "Vetri Dashboard": "/our-product-vetri",
                        };

                        return (
                          <li
                            key={idx}
                            className="hover:scale-105 cursor-pointer"
                            onClick={() => {
                              if (routeMap[link]) {
                                navigate(routeMap[link]);
                                setIsOpen(false);
                              }
                            }}
                          >
                            {link}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="p-6 border-t border-gray-100">
            <button
              onClick={() => {
                navigate("/contact-us");
                setIsOpen(false);
              }}
              className="w-full mt-2 btn2"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavBar;
